import React from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '../../lib/settings/settingsContext';
import { TrendingUp, TrendingDown, DollarSign, Award } from 'lucide-react';
import { LMSRMarketState, PositionWithMarket as ConsolidatedPositionType } from '../../types';
import { lmsrPrice } from '../../lib/lmsrMath.ts';
import { formatShares } from '../../lib/displayFormat.ts';

interface PortfolioSummaryProps {
  portfolioValue: number;
  winRate: number;
  openPositions: number;
  positions: ConsolidatedPositionType[];
  /** Optional: live LMSR state per market — used only as fallback when precomputed unrealized is missing */
  lmsrMarkets?: Record<string, LMSRMarketState>;
  /** Count of settled (won or lost) trades, used for the Win Rate sub-label. */
  settledCount?: number;
}

function lmsrPositionUnrealized(
  pos: ConsolidatedPositionType,
  lmsrMarkets?: Record<string, LMSRMarketState>,
): { pnl: number; usedLivePrice: boolean } {
  if (pos.status !== 'active') return { pnl: 0, usedLivePrice: false };
  const sharesOwned = pos.shares_owned ?? 0;
  if (sharesOwned <= 0) return { pnl: 0, usedLivePrice: false };

  if (pos.engine_type === 'lmsr' && typeof pos.unrealizedPnl === 'number') {
    return { pnl: pos.unrealizedPnl, usedLivePrice: true };
  }

  const marketState = lmsrMarkets?.[pos.marketId];
  if (marketState && marketState.lmsr_b > 0 && marketState.options.length > 0) {
    const optionIdx = marketState.options.findIndex((o) => o.id === pos.market_option_id);
    if (optionIdx >= 0) {
      const allShares = marketState.options.map((o) => o.outstanding_shares);
      const livePrice = lmsrPrice(marketState.lmsr_b, allShares, optionIdx);
      const currentValue = sharesOwned * livePrice;
      return { pnl: currentValue - pos.amountAfterFee, usedLivePrice: true };
    }
  }

  return { pnl: sharesOwned * 1.0 - pos.amountAfterFee, usedLivePrice: false };
}

export function PortfolioSummary({
  portfolioValue,
  winRate,
  openPositions,
  positions,
  lmsrMarkets,
  settledCount = 0,
}: PortfolioSummaryProps) {
  const { formatCurrency } = useSettings();

  const totalPnL = positions.reduce((sum: number, pos: ConsolidatedPositionType) => {
    if (pos.status === 'won') {
      const potentialWin = pos.initial_potential_win ?? pos.amountAfterFee;
      return sum + (potentialWin - pos.amountAfterFee);
    }
    if (pos.status === 'lost') {
      return sum - pos.amountAfterFee;
    }
    return sum;
  }, 0);

  const unrealizedLMSRPnL = positions.reduce((sum: number, pos: ConsolidatedPositionType) => {
    if (pos.engine_type !== 'lmsr') return sum;
    const { pnl } = lmsrPositionUnrealized(pos, lmsrMarkets);
    return sum + pnl;
  }, 0);

  /** One row per market: shares are not comparable across markets, so we never show a single pooled share total. */
  const lmsrRows: {
    marketId: string;
    marketTitle: string;
    totalShares: number;
    unrealized: number;
  }[] = (() => {
    const map = new Map<
      string,
      { marketId: string; marketTitle: string; totalShares: number; unrealized: number }
    >();
    for (const pos of positions) {
      if (pos.status !== 'active') continue;
      if (pos.engine_type !== 'lmsr') continue;
      const sh = pos.shares_owned ?? 0;
      if (sh <= 0) continue;

      const { pnl } = lmsrPositionUnrealized(pos, lmsrMarkets);

      const existing = map.get(pos.marketId);
      if (existing) {
        existing.totalShares += sh;
        existing.unrealized += pnl;
      } else {
        map.set(pos.marketId, {
          marketId: pos.marketId,
          marketTitle: pos.marketTitle,
          totalShares: sh,
          unrealized: pnl,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.marketTitle.localeCompare(b.marketTitle));
  })();

  const hasLMSRPositions = lmsrRows.length > 0;

  const isProfitable = totalPnL >= 0;

  return (
    <div className="space-y-4">
      {hasLMSRPositions && (
        <div className="bg-wayja-light-teal/20 border border-wayja-teal/30 rounded-lg p-4 space-y-3">
          <div>
            <p className="text-sm text-wayja-teal font-medium">Live Portfolio</p>
          </div>

          <ul className="divide-y divide-wayja-teal/20 border border-wayja-teal/20 rounded-md bg-white/60 overflow-hidden">
            {lmsrRows.map((row) => (
              <li key={row.marketId} className="px-3 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/markets/${row.marketId}`}
                    className="text-sm font-medium text-wayja-dark-teal hover:text-wayja-teal hover:underline truncate block"
                    title={row.marketTitle}
                  >
                    {row.marketTitle}
                  </Link>
                  <p className="text-xs text-gray-500 mt-0.5">{formatShares(row.totalShares)}</p>
                </div>
                <div className="text-left sm:text-right flex-shrink-0">
                  <p className="text-xs text-wayja-teal font-medium">Live P/L</p>
                  <p
                    className={`text-sm font-bold ${
                      row.unrealized >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {row.unrealized >= 0 ? '+' : ''}
                    {formatCurrency(Math.abs(row.unrealized))}
                  </p>
                </div>
              </li>
            ))}
          </ul>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 pt-1 border-t border-wayja-teal/25">
            <span className="text-sm font-medium text-wayja-dark-teal">Combined Current Unrealized P/L</span>
            <span
              className={`text-lg font-bold ${
                unrealizedLMSRPnL >= 0 ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {unrealizedLMSRPnL >= 0 ? '+' : ''}
              {formatCurrency(Math.abs(unrealizedLMSRPnL))}
            </span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Portfolio Value */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Portfolio Value</p>
              <h3 className="text-2xl font-bold text-gray-900">{formatCurrency(portfolioValue)}</h3>
              <p className="text-xs text-gray-500 mt-1 max-w-[14rem]">
                Your cash plus the current value of all open positions.
              </p>
              {hasLMSRPositions ? (
                <p className="text-xs text-gray-400 mt-1 max-w-[14rem]">
                  Live-priced positions reflect current quotes; pooled markets use potential payout estimates.
                </p>
              ) : null}
            </div>
            <div className="w-12 h-12 rounded-full bg-wayja-teal/20 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-wayja-teal" />
            </div>
          </div>
        </div>

        {/* Win Rate */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Win Rate</p>
              <h3 className="text-2xl font-bold text-gray-900">{winRate.toFixed(1)}%</h3>
              <p className="text-xs text-gray-500 mt-1">
                Based on {settledCount.toLocaleString()} settled {settledCount === 1 ? 'trade' : 'trades'}
              </p>
            </div>
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              {winRate >= 50 ? (
                <TrendingUp className="w-6 h-6 text-green-600" />
              ) : (
                <TrendingDown className="w-6 h-6 text-red-600" />
              )}
            </div>
          </div>
        </div>

        {/* Open Positions */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Open Positions</p>
              <h3 className="text-2xl font-bold text-gray-900">{openPositions}</h3>
            </div>
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* P/L */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">P/L</p>
              <h3 className={`text-2xl font-bold ${isProfitable ? 'text-green-600' : 'text-red-600'}`}>
                {isProfitable ? '+' : '-'}
                {formatCurrency(Math.abs(totalPnL))}
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Total P/L — all-time, including settled trades.
              </p>
            </div>
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isProfitable ? 'bg-green-100' : 'bg-red-100'
              }`}
            >
              <Award className={`w-6 h-6 ${isProfitable ? 'text-green-600' : 'text-red-600'}`} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
